// src/pages/SettingsPage.js

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import PageContainer from '../components/common/PageContainer';
import OrganizationSettingsForm from './OrganizationSettingsForm'; // New Import
import SubscriptionManagement from './SubscriptionManagement';
import InputField from '../components/InputField'; // Assuming you have this
import CheckboxField from '../components/CheckboxField'; // Assuming you have this
import Button from '../components/Button'; // Assuming you have this
import useApi from '../hooks/useApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';

const SettingsPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('organization'); 
  const api = useApi();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
  }, [location]);

  const GeneralSettingsContent = () => {
    const [settings, setSettings] = useState({
      itemsPerPage: 10,
      currencySymbol: '$',
      currencyCode: 'USD',
      defaultInvoicePaperSize: 'a4',
      taxes: [], // Changed from defaultTaxRate to an array for tax components
      receiptShowLogo: true,
      receiptShowAddress: true,
      receiptShowCustomerName: true, // Defaulting to true as per previous context
      receiptFooterMessage: 'Thank you for your business!',
      // integrations: { someApiKey: '' } // Example for future
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const fetchGeneralSettings = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        // Replace 'settings/general' with your actual API endpoint
        const response = await api.get('settings/general');
        if (response && response.data) {
          const fetchedSettings = response.data;
          // Ensure 'taxes' is an array, migrate from old 'defaultTaxRate' if necessary
          if (fetchedSettings.defaultTaxRate !== undefined && !fetchedSettings.taxes) {
            fetchedSettings.taxes = [{ id: 'default_tax_migrated', name: 'Tax', rate: parseFloat(fetchedSettings.defaultTaxRate) || 0 }];
            delete fetchedSettings.defaultTaxRate; // Remove the old field
          } else if (!Array.isArray(fetchedSettings.taxes)) {
            fetchedSettings.taxes = [];
          }
          // Ensure each tax has an id for React keys
          fetchedSettings.taxes = fetchedSettings.taxes.map((tax, index) => ({ ...tax, id: tax.id || `tax_${Date.now()}_${index}` }));
          setSettings(prev => ({ ...prev, ...fetchedSettings }));
        }
      } catch (err) {
        setError(err.message || 'Failed to load general settings.');
        console.error("Error fetching general settings:", err);
      } finally {
        setLoading(false);
      }
    }, [api]);

    useEffect(() => {
      fetchGeneralSettings();
    }, [fetchGeneralSettings]);

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      setSettings(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value)
      }));
    };

    const handleTaxChange = (index, field, value) => {
      setSettings(prev => {
        const newTaxes = [...prev.taxes];
        newTaxes[index] = {
          ...newTaxes[index],
          [field]: field === 'rate' ? (parseFloat(value) || 0) : value
        };
        return { ...prev, taxes: newTaxes };
      });
    };

    const addTaxComponent = () => {
      setSettings(prev => ({
        ...prev,
        taxes: [
          ...prev.taxes,
          { id: `tax_${Date.now()}`, name: '', rate: 0 } // Use a simple unique ID for new components
        ]
      }));
    };

    const removeTaxComponent = (indexToRemove) => {
      setSettings(prev => ({
        ...prev,
        taxes: prev.taxes.filter((_, index) => index !== indexToRemove)
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      setIsSaving(true);
      setError(null);
      try {
        // Replace 'settings/general' with your actual API endpoint
        await api.put('settings/general', settings);
        alert('General settings saved successfully!');
      } catch (err) {
        setError(err.message || 'Failed to save general settings.');
        console.error("Error saving general settings:", err);
      } finally {
        setIsSaving(false);
      }
    };

    if (loading) return <LoadingSpinner />;
    if (error && !isSaving) return <ErrorMessage message={error} onRetry={fetchGeneralSettings} />;

    return (
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md space-y-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">General Application Settings</h3>
        {error && <ErrorMessage message={error} />}

        <InputField
          label="Default Items Per Page"
          id="itemsPerPage"
          name="itemsPerPage"
          type="number"
          value={settings.itemsPerPage}
          onChange={handleChange}
          min="1"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputField
            label="Currency Symbol"
            id="currencySymbol"
            name="currencySymbol"
            type="text"
            value={settings.currencySymbol}
            onChange={handleChange}
            placeholder="$"
          />
          <InputField
            label="Currency Code (e.g., USD, USD)"
            id="currencyCode"
            name="currencyCode"
            type="text"
            value={settings.currencyCode}
            onChange={handleChange}
            placeholder="USD"
          />
          <div>
            <label htmlFor="defaultInvoicePaperSize" className="block text-sm font-medium text-gray-700 mb-1">
              Default Invoice Paper Size
            </label>
            <select
              id="defaultInvoicePaperSize"
              name="defaultInvoicePaperSize"
              value={settings.defaultInvoicePaperSize}
              onChange={handleChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="a4">A4</option>
              <option value="thermal80">Thermal 80mm</option>
              <option value="thermal58">Thermal 58mm</option>
            </select>
          </div>
        </div>

        <fieldset className="border p-4 rounded-md space-y-3">
          <legend className="text-lg font-medium text-gray-700 px-2">Tax Configuration</legend>
          {settings.taxes.map((tax, index) => (
            <div key={tax.id || index} className="flex flex-col sm:flex-row items-start sm:items-end space-y-2 sm:space-y-0 sm:space-x-3 p-2 border-b border-gray-200 last:border-b-0">
              <InputField
                label={`Tax Component ${index + 1} Name`}
                id={`taxName_${tax.id}`}
                name={`taxName_${index}`} // Name not directly used by handleTaxChange but good for forms
                type="text"
                value={tax.name}
                onChange={(e) => handleTaxChange(index, 'name', e.target.value)}
                placeholder="e.g., CGST, VAT"
                className="flex-grow"
              />
              <InputField
                label="Rate (%)"
                id={`taxRate_${tax.id}`}
                name={`taxRate_${index}`} // Name not directly used by handleTaxChange
                type="number"
                value={tax.rate}
                onChange={(e) => handleTaxChange(index, 'rate', e.target.value)}
                step="0.01"
                min="0"
                className="w-full sm:w-32"
              />
              <Button
                type="button"
                onClick={() => removeTaxComponent(index)}
                variant="danger"
                className="h-10 mt-auto" // Align button with input fields
              >
                Remove
              </Button>
            </div>
          ))}
          <Button type="button" onClick={addTaxComponent} variant="secondary" className="mt-2">Add Tax Component</Button>
        </fieldset>

        <fieldset className="border p-4 rounded-md">
          <legend className="text-lg font-medium text-gray-700 px-2">Receipt Options</legend>
          <CheckboxField label="Show Store Logo on Receipt" id="receiptShowLogo" name="receiptShowLogo" checked={settings.receiptShowLogo} onChange={handleChange} />
          <CheckboxField label="Show Store Address on Receipt" id="receiptShowAddress" name="receiptShowAddress" checked={settings.receiptShowAddress} onChange={handleChange} />
          <CheckboxField label="Show Customer Name on Receipt" id="receiptShowCustomerName" name="receiptShowCustomerName" checked={settings.receiptShowCustomerName} onChange={handleChange} />
          <div>
            <label htmlFor="receiptFooterMessage" className="block text-sm font-medium text-gray-700 mb-1">Receipt Footer Message</label>
            <textarea id="receiptFooterMessage" name="receiptFooterMessage" value={settings.receiptFooterMessage} onChange={handleChange} rows="3"
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
        </fieldset>

        {/* Placeholder for Integrations */}
        <fieldset className="border p-4 rounded-md">
          <legend className="text-lg font-medium text-gray-700 px-2">Integrations</legend>
          <p className="text-gray-600 text-sm">Configuration for third-party integrations will appear here (e.g., payment gateways, accounting software API keys).</p>
          {/* Example: <InputField label="Payment Gateway API Key" id="paymentApiKey" name="integrations.paymentApiKey" type="password" value={settings.integrations?.paymentApiKey || ''} onChange={handleChange} /> */}
        </fieldset>

        <div className="flex justify-end">
          <Button type="submit" variant="primary" isLoading={isSaving} disabled={isSaving}>
            Save General Settings
          </Button>
        </div>
      </form>
    );
  };

  return (
    <Layout>
      <PageContainer title="Application Settings">
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('organization')}
              className={`
                whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'organization'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              Organization Profile
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`
                whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'subscription'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              Subscription
            </button>
            <button
              onClick={() => setActiveTab('general')}
              className={`
                whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm
                ${activeTab === 'general'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              General Settings
            </button>
            
          </nav>
        </div>

        <div>
          {activeTab === 'organization' && <OrganizationSettingsForm />}
          {activeTab === 'general' && <GeneralSettingsContent />}
          {activeTab === 'subscription' && <SubscriptionManagement />}
        </div>
      </PageContainer>
    </Layout>
  );
};

export default SettingsPage;
